import { useRef } from 'react';

import { trackAthenaAddToWishlist } from '@/common/analytics/athena/trackAddToWishlist';
import { trackAthenaArtworkClick } from '@/common/analytics/athena/trackArtworkClick';
import { trackGA4SelectItem } from '@/common/analytics/gtm/item/trackSelectItem';
import { trackUAProductClick } from '@/common/analytics/gtm/product/trackProductClick';
import { EventMessage } from '@/common/analytics/gtm/types';
import {
  trackGA4AddToWishlist,
  trackGA4RemoveFromWishlist,
  trackUAAddToWishlist,
  trackUARemoveFromWishlist,
} from '@/common/analytics/gtm/wishlist/trackWishlistAction';
import { mapItemToEventMessage } from '@/common/analytics/utils';
import { ArtworkPicture } from '@/common/components/ArtworkPicture/ArtworkPicture';
import { mansonryArtworkSets } from '@/common/components/ArtworkPicture/artworksBreakpoints.config';
import {
  MasonryGridItemContainer,
  WishlistButtonWrapper,
  MasonryGridImageWrapper,
} from '@/common/components/MasonryFeed/MasonryFeeds.styled';
import RouterLink from '@/common/components/RouterLink/RouterLink';
import { ComponentMetadata } from '@/common/components/types';
import { WishlistButton } from '@/common/components/WishlistButton/WishlistButton';
import { useAthena } from '@/common/hooks/useAthena/useAthena';
import { useImpression } from '@/common/impressions';
import { RouteToProductPage } from '@/modules/ProductPage/ProductPageConfig';
import { GalleryItem } from 'generatedBfw/static';

interface MasonryGridItemProps {
  index: number;
  item: GalleryItem;
  metadata: ComponentMetadata;
}

export const MasonryGridItem = ({ index, item, metadata }: MasonryGridItemProps) => {
  const imageRef = useRef(null);
  const eventMessage = mapItemToEventMessage({ index, item, metadata });
  const { setOnHold } = useImpression<EventMessage>({
    ref: imageRef,
    message: eventMessage,
    initialOnHold: true,
  });
  const { pushEvent } = useAthena();

  return (
    <MasonryGridItemContainer $orientation={item.orientation} data-testid="masonry-grid-item">
      <WishlistButtonWrapper>
        <WishlistButton
          color="white"
          externalId={item.externalId}
          onAddToWishlist={() => {
            trackUAProductClick(eventMessage);
            trackUAAddToWishlist(eventMessage);
            trackGA4SelectItem(eventMessage);
            trackGA4AddToWishlist(eventMessage);
            trackAthenaAddToWishlist(eventMessage, pushEvent);
          }}
          onRemoveFromWishlist={() => {
            trackUAProductClick(eventMessage);
            trackUARemoveFromWishlist(eventMessage);
            trackGA4SelectItem(eventMessage);
            trackGA4RemoveFromWishlist(eventMessage);
          }}
        />
      </WishlistButtonWrapper>
      <RouterLink
        className="masonryGridItemLink"
        href={
          RouteToProductPage({
            externalId: item.externalId.toString(),
          }).route
        }
        onClick={() => {
          trackGA4SelectItem(eventMessage);
          trackUAProductClick(eventMessage);
          trackAthenaArtworkClick(eventMessage, pushEvent);
        }}
      >
        <MasonryGridImageWrapper ref={imageRef}>
          <ArtworkPicture
            artworkOrientation={item.orientation}
            artworkSets={mansonryArtworkSets}
            imageCoreUrl={item.imageCoreUrl}
            loading="lazy"
            onLoaded={() => setOnHold(false)}
          />
        </MasonryGridImageWrapper>
      </RouterLink>
    </MasonryGridItemContainer>
  );
};
